(() => {
  'use strict';
  const managed = /^COMBO[246]$/i;
  const tier = count => count >= 6 ? {code:'COMBO6',saving:30000} : count >= 4 ? {code:'COMBO4',saving:15000} : count >= 2 ? {code:'COMBO2',saving:5000} : {code:'',saving:0};
  const codes = cart => [...new Set([
    ...(cart.discount_codes || []).map(entry => entry.code),
    ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).filter(entry => entry.discount_application?.type === 'discount_code').map(entry => entry.discount_application.title)),
    ...(cart.cart_level_discount_applications || []).filter(entry => entry.type === 'discount_code').map(entry => entry.title)
  ].filter(Boolean).map(code => code.toUpperCase()))];
  const savings = cart => (cart.cart_level_discount_applications || []).filter(entry => managed.test(entry.title || '')).reduce((sum,entry) => sum + Number(entry.total_allocated_amount || 0),0) + (cart.items || []).reduce((sum,item) => sum + (item.line_level_discount_allocations || []).filter(entry => managed.test(entry.discount_application?.title || '')).reduce((total,entry) => total + Number(entry.amount || 0),0),0);
  const count = (cart, _eligible) => (cart.items || []).reduce((sum,item) => sum + (!item.selling_plan_allocation && !item.gift_card ? Math.max(0,Math.floor(Number(item.quantity) || 0)) : 0),0);
  const offer = (cart, _products) => tier(count(cart));
  const beforeCoupon = cart => Number(cart.original_total_price ?? (Number(cart.total_price || 0) + Number(cart.total_discount || 0)));
  const codFee = subtotal => Math.max(5000,Math.round(Number(subtotal || 0) * 0.05 / 100) * 100);
  const shipping = (amount,threshold = 34900,flat = 6800) => Number(amount) >= threshold ? 0 : flat;
  const payable = (cart,{threshold = 34900,flat = 6800,domestic = true} = {}) => {
    const subtotal = beforeCoupon(cart);
    const fee = codFee(subtotal);
    const needsShipping = cart.requires_shipping !== false && Number(cart.item_count) > 0;
    const onlineShipping = domestic ? (needsShipping ? shipping(cart.total_price,threshold,flat) : 0) : null;
    const codShipping = domestic ? (needsShipping ? shipping(subtotal,threshold,flat) : 0) : null;
    return {subtotal,fee,onlineShipping,codShipping,onlineTotal:Number(cart.total_price) + (onlineShipping || 0),codTotal:subtotal + fee + (codShipping || 0)};
  };
  const policy = {tier,codes,savings,count,offer,beforeCoupon,codFee,shipping,payable};
  if (typeof module !== 'undefined' && module.exports) module.exports = policy;
  if (typeof document === 'undefined') return;
  const node = document.getElementById('IshvaryaComboConfig');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  if (Number(config.themeId) !== Number(config.couponThemeId)) return;
  const eligible = [...config.products.dhoop,...config.products.incense];
  const money = amount => new Intl.NumberFormat('en-IN',{style:'currency',currency:config.currency,maximumFractionDigits:0,minimumFractionDigits:0}).format(amount / 100);
  const message = (key,values = {}) => Object.entries(values).reduce((result,[key,value]) => result.replaceAll('__' + key.toUpperCase() + '__',value),config.text[key] || key);
  const request = async (path,body) => {
    const response = await fetch(config.root + path,{method:body ? 'POST' : 'GET',credentials:'same-origin',cache:'no-store',headers:{Accept:'application/json','Content-Type':'application/json'},...(body ? {body:JSON.stringify(body)} : {})});
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || config.text.coupon_error);
    return result;
  };
  let queue = Promise.resolve();
  let lastCart;
  let lastError = '';
  const display = cart => {
    if (!cart) return;
    lastCart = cart;
    for (const drawer of document.querySelectorAll('[data-ishvarya-cart-drawer],[data-ishvarya-header-cart-drawer]')) {
      const panel = drawer.querySelector('.ishvarya-cart-drawer__panel');
      const footer = panel?.querySelector('.ishvarya-cart-drawer__footer');
      if (panel && footer && !panel.querySelector('[data-ish-cart-scroll]')) {
        const body = document.createElement('div');
        body.className = 'ishvarya-cart-drawer__scroll';
        body.dataset.ishCartScroll = '';
        body.tabIndex = 0;
        body.setAttribute('role','region');
        body.setAttribute('aria-label',config.text.bag_details);
        const actions = document.createElement('div');
        actions.className = 'ishvarya-cart-drawer__actions';
        actions.dataset.ishCartActions = '';
        for (const control of footer.querySelectorAll('.ishvarya-cart-drawer__checkout,.ishvarya-cart-drawer__continue')) actions.append(control);
        const content = [...panel.children].filter(child => !child.classList.contains('ishvarya-cart-drawer__header'));
        panel.append(body);
        for (const child of content) body.append(child);
        body.append(actions);
      }
      const actions = panel?.querySelector('[data-ish-cart-actions]');
      if (actions) actions.hidden = !cart.item_count;
      const subtotal = drawer.querySelector('[data-cart-subtotal],[data-header-cart-subtotal]');
      if (subtotal && !drawer.querySelector('[data-combo-discount-summary]')) {
        const panel = document.createElement('aside');
        panel.dataset.comboDiscountSummary = '';
        panel.className = 'ish-coupon-summary';
        subtotal.parentElement.before(panel);
      }
    }
    const discountMap = new Map();
    for (const discount of cart.cart_level_discount_applications || []) discountMap.set(discount.title,(discountMap.get(discount.title) || 0) + Number(discount.total_allocated_amount || 0));
    for (const item of cart.items || []) for (const allocation of item.line_level_discount_allocations || []) {
      const title = allocation.discount_application?.title;
      if (title) discountMap.set(title,(discountMap.get(title) || 0) + Number(allocation.amount || 0));
    }
    const discounts = [...discountMap].map(([title,total_allocated_amount]) => ({title,total_allocated_amount}));
    const subtotal = beforeCoupon(cart);
    const domestic = config.shippingCountry === 'IN' && config.currency === 'INR';
    const totals = payable(cart,{threshold:config.shippingThreshold,flat:config.shippingFlat,domestic});
    const shippingLabel = domestic ? config.text.shipping_label_india : config.text.shipping_label;
    const totalLabel = domestic ? config.text.coupon_after : config.text.payable_before_shipping;
    const shippingText = amount => amount === null ? config.text.shipping_checkout : amount === 0 ? config.text.shipping_free : money(amount);
    for (const panel of document.querySelectorAll('[data-combo-discount-summary]')) {
      panel.replaceChildren();
      panel.hidden = !cart.item_count;
      panel.setAttribute('aria-live','polite');
      if (panel.hidden) continue;
      const heading = document.createElement('strong');
      heading.className = 'ish-coupon-summary__title';
      heading.textContent = config.text.coupon_summary;
      panel.append(heading);
      const group = label => {
        const section = document.createElement('section');
        section.className = 'ish-payment-summary';
        const title = document.createElement('h3');
        title.textContent = label;
        section.setAttribute('aria-label',label);
        section.append(title);
        panel.append(section);
        return section;
      };
      const row = (section,label,amount,total = false) => {
        const line = document.createElement('p');
        if (total) line.className = 'ish-payment-summary__total';
        const title = document.createElement('span');
        const value = document.createElement('strong');
        title.textContent = label;
        value.textContent = amount;
        line.append(title,value);
        section.append(line);
      };
      const online = group(config.text.online_payment);
      row(online,config.text.coupon_before,money(subtotal));
      for (const discount of discounts) row(online,discount.title + ' · ' + config.text.coupon_label,'−' + money(discount.total_allocated_amount));
      if (!discounts.length) row(online,config.text.discount_label,money(0));
      row(online,shippingLabel,shippingText(totals.onlineShipping));
      row(online,totalLabel,money(totals.onlineTotal),true);
      const cod = group(config.text.cod_payment);
      row(cod,config.text.coupon_before,money(subtotal));
      row(cod,config.text.cod_discount,money(0));
      row(cod,config.text.cod_fee,'+' + money(totals.fee));
      row(cod,shippingLabel,shippingText(totals.codShipping));
      row(cod,domestic ? config.text.cod_total : config.text.payable_before_shipping,money(totals.codTotal),true);
      const note = document.createElement('small');
      note.className = 'ish-payment-summary__note';
      if (lastError) note.textContent = lastError;
      else {
        const notice = document.createElement('strong');
        notice.textContent = config.text.payment_notice;
        note.append(notice,document.createTextNode(' ' + (domestic ? config.text.payment_conditions : config.text.payment_conditions_international)));
      }
      panel.append(note);
      if (lastError) {
        panel.setAttribute('role','alert');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = config.text.coupon_retry;
        retry.addEventListener('click',() => syncCart().catch(() => {}));
        panel.append(retry);
      } else panel.removeAttribute('role');
    }
    for (const subtotal of document.querySelectorAll('[data-header-cart-subtotal], [data-cart-drawer-items] ~ * [data-cart-subtotal]')) subtotal.textContent = money(cart.total_price);
  };
  const reconcile = async () => {
    let cart = await request('cart.js');
    let changed = false;
    try {
      const wanted = offer(cart,config.products);
      const existing = codes(cart);
      const desired = [...existing.filter(code => !managed.test(code)),...(wanted.code ? [wanted.code] : [])];
      const equal = [...existing].sort().join(',') === [...desired].sort().join(',');
      const applied = savings(cart);
      if (!equal || (wanted.code && applied < Math.min(wanted.saving,beforeCoupon(cart)))) {
        cart = await request('cart/update.js',{discount:desired.join(',')});
        cart = await request('cart.js');
        changed = true;
      }
      for (let attempt = 0; attempt < 2 && wanted.code && savings(cart) < Math.min(wanted.saving,cart.items_subtotal_price); attempt++) {
        await new Promise(resolve => setTimeout(resolve,150));
        cart = await request('cart.js');
      }
      const current = offer(cart,config.products);
      if (current.code !== wanted.code || (current.code && savings(cart) < Math.min(current.saving,cart.items_subtotal_price))) {
        console.warn('Combo coupon verification',{wanted:wanted.code,current:current.code,saving:savings(cart),subtotal:cart.items_subtotal_price,codes:codes(cart)});
        throw new Error(config.text.coupon_error);
      }
      const cookie = document.cookie.split('; ').find(entry => entry.startsWith('discount_code='));
      let oldCode = '';
      try {oldCode = cookie ? decodeURIComponent(cookie.slice('discount_code='.length)) : '';} catch (_) {}
      if (current.code) document.cookie = 'discount_code=' + encodeURIComponent(current.code) + '; Path=/; SameSite=Lax; Secure';
      else if (managed.test(oldCode)) document.cookie = 'discount_code=; Path=/; Max-Age=0; SameSite=Lax; Secure';
      lastError = '';
      display(cart);
      document.dispatchEvent(new CustomEvent('ishvarya:coupon-synced',{detail:cart}));
      if (changed) document.dispatchEvent(new CustomEvent('ishvarya:coupon-changed',{detail:cart}));
      return cart;
    } catch (error) {
      lastError = config.text.coupon_error;
      display(cart);
      throw error;
    }
  };
  const syncCart = () => {
    const operation = queue.then(reconcile);
    queue = operation.catch(() => {});
    return operation;
  };
  window.IshvaryaComboDiscounts = {...policy,syncCart,display};
  document.addEventListener('ishvarya:coupon-synced',event => display(event.detail));
  let scheduled;
  const schedule = () => {clearTimeout(scheduled);scheduled = setTimeout(() => syncCart().catch(() => {}),250);};
  for (const name of ['cart:updated','cart:refresh','ishvarya:cart-updated','ishvarya:combo-added']) document.addEventListener(name,schedule);
  window.addEventListener('pageshow',schedule);
  const replay = new WeakSet();
  const checkout = async (event,control,resume) => {
    if (replay.has(control)) {replay.delete(control);return;}
    event.preventDefault();
    event.stopImmediatePropagation();
    if (control.dataset.comboChecking === 'true') return;
    control.dataset.comboChecking = 'true';
    control.setAttribute('aria-busy','true');
    try {
      await syncCart();
      replay.add(control);
      resume();
    } catch (_) {
      const panel = document.querySelector('[data-combo-discount-summary][role="alert"]');
      panel?.scrollIntoView({block:'center',behavior:'smooth'});
    } finally {
      control.dataset.comboChecking = 'false';
      control.removeAttribute('aria-busy');
    }
  };
  document.addEventListener('click',event => {
    const control = event.target.closest('a[href*="/checkout"],button[name="checkout"],.sr-headless-checkout');
    if (control) checkout(event,control,() => control.click());
  },true);
  document.addEventListener('submit',event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout') checkout(event,form,() => form.requestSubmit(event.submitter));
  },true);
  let frame;
  new MutationObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const missing = [...document.querySelectorAll('[data-ishvarya-cart-drawer],[data-ishvarya-header-cart-drawer]')].some(drawer => !drawer.querySelector('[data-combo-discount-summary]'));
      if (missing && lastCart) display(lastCart);
    });
  }).observe(document.body,{childList:true,subtree:true});
})();
